import { useRef, useState } from "react";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import type { Timer } from "app/pages/sales-intelligence/types";

import {
    SALESLOFT_CONNECT_POLL_INTERVAL_MS,
    SALESLOFT_CONNECT_POPUP_HEIGHT,
    SALESLOFT_CONNECT_POPUP_WIDTH,
    SALESLOFT_CONNECT_URL,
} from "../constants/salesloftConstants";

type UseSalesloftConnectParams = {
    onWindowClosed: () => void;
};

/**
 * Opens the Salesloft OAuth connect flow in a popup and runs `onWindowClosed`
 * once the user closes it — mirrors the CRM connect-button popup/poll pattern.
 */
export const useSalesloftConnect = ({ onWindowClosed }: UseSalesloftConnectParams) => {
    const [connectWindow, setConnectWindow] = useState<Window | null>(null);

    // Keep the latest callback so the poll interval never invokes a stale closure.
    const onWindowClosedRef = useRef(onWindowClosed);
    onWindowClosedRef.current = onWindowClosed;

    useDidUpdate(() => {
        let timer: Timer;
        if (connectWindow) {
            timer = setInterval(() => {
                if (connectWindow.closed) {
                    onWindowClosedRef.current();
                    clearInterval(timer);
                }
            }, SALESLOFT_CONNECT_POLL_INTERVAL_MS);
        }

        return () => {
            clearInterval(timer);
            setConnectWindow(null);
        };
    }, [connectWindow]);

    const openSalesloftConnect = () => {
        const left = (screen.width - SALESLOFT_CONNECT_POPUP_WIDTH) / 2;
        const top = (screen.height - SALESLOFT_CONNECT_POPUP_HEIGHT) / 4;
        const opened = window.open(
            SALESLOFT_CONNECT_URL,
            "_blank",
            // eslint-disable-next-line max-len
            `location=0,status=0,width=${SALESLOFT_CONNECT_POPUP_WIDTH}px,height=${SALESLOFT_CONNECT_POPUP_HEIGHT}px,resizable=yes,left=${left},top=${top}`,
        );
        setConnectWindow(opened);
    };

    return { openSalesloftConnect };
};
